import { instanceToInstance, Transform, Type } from "class-transformer";
import { FacilityLocation, ProviderLocationIdentifier } from "./facility";
import { ILocation, Location } from "./location";
import { IParty, Party } from "./party";
import { Diagnosis, ICategory, IDiagnosis, IModifier, IProcedure, Modifier, Procedure } from "./procedure";
import { Professional } from "./professional";
import { HarloweSupport } from "../models/support";
import { Encounter } from "./encounter";

export interface IMedicalBill
{
    id:number;
    lines:IMedicalBillLine [];
    patient: IParty | undefined;
    patientAddress: ILocation | undefined;
    phoneNumber: string;
    patientAccountNumber: string;
    gender: string;
    insuredName: string;
    insuredAddress: ILocation | undefined;
    insuredPhone: string;
    servicingProvider: FacilityLocation | undefined;
    billingProvider: FacilityLocation | undefined ;
    referringProvider: Professional | undefined;
    serviceBegin: Date;
    serviceEnd: Date;
    totalCharges: number;
    medicalBillImage?: HarloweSupport.File;
}

export interface IMedicalBillLine
{
    id: number;
    procedure: IProcedure;
    units: number;
    modifiers: IModifier[];
    associatedDiagnosis: IDiagnosis[];
    dateOfService: Date;
    cost: number;
    unitCost: number;

}

export class MedicalBillLine implements IMedicalBillLine
{
    constructor
    (
        public id: number,
        procedure: Procedure,
        public units: number,
        public modifiers: Modifier[],
        public associatedDiagnosis: Diagnosis[],
        dateOfService: Date,
        public cost: number,
        public unitCost: number
    )
    {
        this.procedure = procedure;
        this.dateOfService = dateOfService;
    }
    @Type(() => Procedure)
    procedure: Procedure;

    @Type(() => Date)
    dateOfService: Date;
}

export class MedicalBillLineInsert
{
    constructor
    (
        public procedureId: number,
        public units: number,
        public modifierCodes: string[],
        public dateOfService: Date,
        public cost: number,
        public associatedDiagnosisCodes: string[]
    )
    {}

}

export class MedicalBillLineUpdate
{
    constructor
    (
        public id: number
    )
    {}

    procedureId?: number;
    units?: number;
    modifierCodes?: string[];
    associatedDiagnosisCodes?: string[];
    dateOfService?: Date;
    cost?: number
}


export class MedicalBill<E extends Encounter> implements IMedicalBill
{
    constructor
    (
        public id:number,
        lines:MedicalBillLine [],
        patient: Party,
        public patientAddress: Location,
        public phoneNumber: string,
        public gender: string,
        public insuredName: string,
        public insuredAddress: Location,
        public insuredPhone: string,
        servicingProvider: FacilityLocation,
        billingProvider: FacilityLocation,
        public referringProvider: Professional,
        public serviceBegin: Date,
        public serviceEnd: Date,
        public totalCharges: number,
        public patientAccountNumber: string,
        encounter: E,
        adjustments: AdjustedMedicalBill<E>[],
        public category: ICategory
        
    )
    {
        this.lines = lines;
        this.servicingProvider = servicingProvider;
        this.billingProvider = billingProvider;
        this.patient = patient;
        this.case = encounter;
        this.adjustments = adjustments;
    }

    @Type(() => Party)
    patient:Party;
    @Type(() => MedicalBillLine)
    lines:MedicalBillLine [];

    @Type(() => FacilityLocation)
    servicingProvider: FacilityLocation;

    @Type(() => FacilityLocation)
    billingProvider: FacilityLocation;


    @Type(() => Encounter)
    case: E;

    @Type(() => AdjustedMedicalBill<E>)
    adjustments: AdjustedMedicalBill<E>[]

    submissionDate?: Date;

    medicalBillImage?: HarloweSupport.File;
    billThumbnail?: HarloweSupport.File;
}



export class MedicalBillInsert
{
    constructor
    (
        public patientId: number,
        public patientAddressId: number,
        public phoneNumber: string,
        public gender: string,
        public patientAccountNumber: string,
        public insuredPhone: string,
        public insuredName: string,
        public insuredAddressId: number,
        public servicingProvider: ProviderLocationIdentifier,
        public billingProvider: ProviderLocationIdentifier,
    )
    {}

    billingLines: MedicalBillLineInsert[] = []
    referringProviderNPI?: string 
}

export class MedicalBillUpdate
{
    constructor
    (
        public id: number
    )
    {}

    patientId?: number;
    patientAddressId?: number;
    patientAccountNumber?: string;
    phoneNumber?: string;
    gender?: string;
    insuredPhone?: string;
    public insuredName?: string;
    public insuredAddressId?: number;
    servicingProvider?: ProviderLocationIdentifier;
    billingProvider?: ProviderLocationIdentifier;
    referringProviderNPI?: string;
    serviceBegin?: Date;
    serviceEnd?: Date;    
    overrideDiscount?: boolean;
    discountOverridePercent?: number;
    overrideMarkup?: boolean;
    markupPercentOfCharges?: number;

}

export class MedicalBillSubmission
{
    constructor
    (
        public id: number,
        public billImage: HarloweSupport.File,
        public billThumbnail: HarloweSupport.File,
        public dateSubmitted: Date,
        public submittedById: number
    )
    {

    }

    submissionDate?: Date;

}

export class MedicalBillIssue
{
    constructor
    (
        public fieldName: string,
        public issueText: string
    )
    {

    }

    get displayText() : string
    {
        return `${this.fieldName} - ${this.issueText}`;
    }
}


// export interface IDraftMedicalBill
// {
//     id:number,
//     lines:DraftMedicalBillLine [],
//     patient: Party | undefined,
//     patientAddress: Location | undefined,
//     phoneNumber: string | undefined,
//     gender: string | undefined,
//     insuredName: string | undefined,
//     insuredAddress: Location | undefined,
//     insuredPhone: string | undefined,
//     servicingProvider: FacilityLocation | undefined,
//     billingProvider: FacilityLocation | undefined,
//     referringProvider: Professional | undefined,
//     serviceBegin: Date | undefined,
//     serviceEnd: Date | undefined,
//     totalCharges: number | undefined,
//     patientAccountNumber: string | undefined,
//     processingErrors: MedicalBillIssue[],
//     billComplete: boolean,
//     billSubmission: MedicalBillSubmission,
//     confirmed: boolean
// }

export class DraftMedicalBill
{
    constructor
    (
        public id:number | undefined,
        lines:DraftMedicalBillLine [] = [],
        patient: Party | undefined,
        patientAddress: Location | undefined,
        public phoneNumber: string | undefined,
        public gender: string | undefined,
        public insuredName: string | undefined,
        public insuredAddress: Location | undefined,
        public insuredPhone: string | undefined,
        servicingProvider: FacilityLocation | undefined,
        billingProvider: FacilityLocation | undefined,
        referringProvider: Professional | undefined,
        public serviceBegin: Date | undefined,
        public serviceEnd: Date | undefined,
        public totalCharges: number | undefined,
        public patientAccountNumber: string | undefined,
        processingErrors: MedicalBillIssue[] = [],
        public billComplete: boolean,
        billSubmission: MedicalBillSubmission,
        public confirmed: boolean = false
    )
    {
        // super(id,lines,patient,patientAddress,phoneNumber,gender,insuredName,insuredAddress,insuredPhone,servicingProvider,billingProvider,referringProvider,serviceBegin,serviceEnd,totalCharges,patientAccountNumber);
        this.patient = patient;
        this.lines = lines;
        this.servicingProvider = servicingProvider;
        this.billingProvider = billingProvider;
        this.referringProvider = referringProvider;
        this.patientAddress = patientAddress;
        this.billSubmission = billSubmission;
        this.processingErrors = processingErrors;
    }

    @Type(() => MedicalBillSubmission)
    billSubmission: MedicalBillSubmission;

    @Type(() => DraftMedicalBillLine)
    lines:DraftMedicalBillLine [] = [];

    @Type(() => Party)
    patient: Party | undefined;

    @Type(() => FacilityLocation)
    servicingProvider: FacilityLocation | undefined;

    @Type(() => FacilityLocation)
    billingProvider: FacilityLocation | undefined;

    @Type(() => Professional)
    referringProvider: Professional | undefined;

    @Type(() => Location)
    patientAddress: Location | undefined;

    @Type(() => MedicalBillIssue)
    processingErrors: MedicalBillIssue[] = [];

    submissionDate?: Date;

    get totalLinesCost() : number
    {
        return this.lines.reduce((a, c) => a + c.cost,0);    
    }

    get totalUnits() : number
    {
        return this.lines.reduce((a, c) => a + (c.units || 0),0);    
    }

    get allProcessingErrors() : MedicalBillIssue []
    {
        return [... this.processingErrors, ...this.lines.reduce((a,c)=> [...a,...c.processingErrors],[] as MedicalBillIssue[]) ]
    }

    createMedicalBillInsert() : MedicalBillInsert | undefined
    {
        if(this.patient && this.patientAddress && this.phoneNumber && this.gender && this.patientAccountNumber && this.insuredPhone &&
            this.insuredName && this.insuredAddress && this.servicingProvider && this.billingProvider)
        {
            const mbi = new MedicalBillInsert
            (
                this.patient.id,
                this.patientAddress.id,
                this.phoneNumber,
                this.gender,
                this.patientAccountNumber,
                this.insuredPhone,
                this.insuredName,
                this.insuredAddress.id,
                this.servicingProvider.generateProviderLocationIdentifier(),
                this.billingProvider.generateProviderLocationIdentifier()
            );

            mbi.billingLines = this.lines.map
            (
                l =>
                {
                    if(l.procedure && l.units)
                        return new MedicalBillLineInsert(l.procedure.id,l.units,l.modifiers?.map(m => m.code) ?? [],l.dateOfService,l.cost,l.associatedDiagnosis.map(d => d.code))
                    return undefined
                }
            ).filter(l => l != undefined) as MedicalBillLineInsert[];

            if(this.referringProvider)
                mbi.referringProviderNPI = this.referringProvider.npi;

            return mbi;
        }

        return undefined;
    }
}



export class DraftMedicalBillLine
{
     constructor
    (
        public id: number | undefined,
        procedure: Procedure | undefined,
        public units: number | undefined,
        modifiers: Modifier[] = [],
        associatedDiagnosis: Diagnosis[],
        dateOfService: Date,
        public cost: number,
        public unitCost: number,
        processingErrors: MedicalBillIssue[] = []
    )
    {
        // super(id,procedure,units,modifiers,associatedDiagnosis,dateOfService,cost,unitCost);
        this.procedure = procedure;
        this.modifiers = modifiers;
        this.associatedDiagnosis = associatedDiagnosis;
        this.processingErrors = processingErrors;
        this.dateOfService = dateOfService;
    }


    @Type(() => Procedure)
    procedure: Procedure | undefined;

    @Type(() => Modifier)
    modifiers: Modifier[] = [];

    @Type(() => Diagnosis)
    associatedDiagnosis: Diagnosis[] = [];

    @Type(() => MedicalBillIssue)
    processingErrors: MedicalBillIssue[] = [];

    @Type(() => Date)
    dateOfService: Date;

}

export enum BillDiscountType
{
    FlatAmountByCategory,
    FlatAmountPerBillDiscount,
    PercentOfCharges,
    FlatAmountByLineItem,
    DiscountOffOfCharges,
    NoDiscount,
    FullDiscount,
    ManualAmount,
    ManualPercent,
    FlatAmountByCategoryUnit
}

export class ProviderMarkup
{
    constructor
    (
        public id: number,
        markupType: number,
        public amount: number,
        public category: ICategory,
        public active: boolean,
        public effectiveDate: Date,
        public expirationDate: Date
    )
    {
        this.markupType = markupType;
    }

    @Transform(({ value }) => { return value as BillMarkupType})
    markupType: BillMarkupType;

    get displayName() : string
    {
        switch (this.markupType)
        {
            case BillMarkupType.FixedAmount:
                return `Markup Charges: ${this.amount}%`
            case BillMarkupType.FlatAmountByCategory:
                return `Flat Charge For ${this.category.categoryName}: $${this.amount}`
            case BillMarkupType.PercentOfBilled:
                return `Percent Of Billed: ${this.amount}%`
            case BillMarkupType.FlatAmountByCategoryUnit:
                return `Flat Charge Per Unit of ${this.category.categoryName}: $${this.amount}`
            default:
                return 'No Markup'
        } 
    }

}

export class ProviderDiscount
{
    constructor
    (
        public id: number,
        discountType: number,
        public amount: number,
        public category: ICategory,
        public active: boolean,
        public effectiveDate: Date,
        public expirationDate: Date
    )
    {
        this.discountType = discountType;
    }

    @Transform(({ value }) => { return value as BillDiscountType})
    discountType: BillDiscountType;

    get displayName() : string
    {
        switch (this.discountType)
        {
            case BillDiscountType.DiscountOffOfCharges:
                return `Discount Off Charges: ${this.amount}%`
            case BillDiscountType.FlatAmountByCategory:
                return `Flat Charge For ${this.category.categoryName}: $${this.amount}`
            case BillDiscountType.FlatAmountPerBillDiscount:
                return `Flat Charge Per Bill: $${this.amount}`
            case BillDiscountType.PercentOfCharges:
                return `Percent Of Charges: ${this.amount}%`
            case BillDiscountType.FlatAmountByLineItem:
                return `Charge Per Line: $${this.amount}`
            case BillDiscountType.FlatAmountByCategoryUnit:
                return `Flat Charge Per Unit of ${this.category.categoryName}: $${this.amount}`
            default:
                return 'No Discount'
        } 
    }

}

export class BillDiscount
{
    constructor
    (
        public discountType: BillDiscountType,
        public category: ICategory,
        public discountAmount: number,
        public isManual: boolean
    )
    {

    }
}


export class BillMarkup
{
    constructor
    (
        public markupType: BillMarkupType,
        public isManual: boolean,
        public amount: number,
        public markupBasedOn: MarkupBasedOn
    )
    {   

    }
}


export enum BillMarkupType
{
    PercentOfBilled,
    FixedAmount,
    ManualPercent,
    ManualAmount,
    FlatAmountByCategoryUnit,
    FlatAmountByCategory
}

export enum MarkupBasedOn
{
    OriginalAmount,
    AdjustedAmount
}
export class AdjustedMedicalBillLine
{
    constructor
    (
        public lineId: number,
        public code: string,
        public units: number,
        public originalPrice: number,
        public adjustment: number,
        public adjustedPrice: number,
        public separateProcedure: boolean
    )
    {
    }
}

export class AdjustedMedicalBill<E extends Encounter>
{
    constructor
    (
        // public originalBill: MedicalBill<E>,
        public totalAdjustedDiscountAmount: number,
        public totalAdjustedAmount: number,
        public billDiscount: BillDiscount,
        public id: number,
        public voided: boolean,
        public voidDate: Date,
        public originalBillId: number,
        repricings: RepricedMedicalBill<E>[],
        lines: AdjustedMedicalBillLine []
    )
    {
        this.repricings = repricings
        this.lines = lines;
    }

    @Type(() => RepricedMedicalBill<E>)
    repricings: RepricedMedicalBill<E>[];

    @Type(() => AdjustedMedicalBillLine)
    lines: AdjustedMedicalBillLine [];


    get activeRepricing() : RepricedMedicalBill<E> | undefined
    {
        return instanceToInstance(this.repricings.find(r => !r.voided));
    }

}

export class RepricedMedicalBillLine
{
    constructor
    (
        public lineId: number,
        public code: string,
        public units: number,
        public markupType: MarkupBasedOn,
        public originalPrice: number,
        public repricedAmount: number,
        public totalMarkup: number,
        public separateProcedure: boolean
    )
    {
    }
}

export class RepricedMedicalBill<E extends Encounter>
{
    constructor
    (
        public appliedMarkup: BillMarkup,
        public totalRepricedAmount: number,
        public totalRepricedMarkupAmount: number,
        public effectiveMarkupPercent: number,
        public gain: number,
        public id: number,
        public voided: boolean,
        public voidDate: Date,
        public originalBillId: number,
        public adjustmentId: number,
        public submittedToPayer: boolean,
        public payerSubmissionDate: Date | undefined,
        lines: RepricedMedicalBillLine []
    )
    {
        this.lines = lines;
    }


    @Type(() => RepricedMedicalBillLine)
    lines: RepricedMedicalBillLine [];

}

export class EDIPayer
{
    constructor
    (
        public id: number,
        public name: string
    )
    {

    }
}

export class EDISender
{
    constructor
    (
        public id: number,
        public name: string
    )
    {
        
    }
}
